/* eslint-disable max-lines */
import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import axios from 'axios';
import { styled } from '@this/constants/themes';
import { observer } from 'mobx-react';
import type { Moment } from 'moment';
import moment from 'moment';
import muiTheme from '@this/components/appoint/mui_theme';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import Paper from '@material-ui/core/Paper';
import CancelIcon from '@material-ui/icons/Cancel';
import type { OpenMeetingResponseArgs } from '@this/domain/appoint/open_meeting';
import OpenMeeting, {
  convertOpenMeetingResponseToArgs,
  initialOpenMeetingArgs
} from '@this/domain/appoint/open_meeting';
import type { CalendarArgs } from '@this/domain/appoint/calendar';
import Calendar from '@this/domain/appoint/calendar';
import type { MeetingTitleHistoryArgs } from '@this/domain/appoint/meeting_title_history';
import MeetingTitleHistory from '@this/domain/appoint/meeting_title_history';
import type { EventResponse } from '@this/domain/appoint/event';
import Event, { convertEventResponseToArgs } from '@this/domain/appoint/event';
import type OpenMeetingBlockedSlot from '@this/domain/appoint/open_meeting_blocked_slot';
import type { UserSettingResponse } from '@this/domain/appoint/user_setting';
import UserSetting, { convertUserSettingResponseToArgs } from '@this/domain/appoint/user_setting';
import Utils from '@this/domain/appoint/utils';
import AppointContext from '@this/components/appoint/appoint_context';
import Form from '@this/components/appoint/open_meetings/form/form';
import AuthBackdrop from '@this/components/appoint/open_meetings/auth_backdrop/auth_backdrop';
import BigCalendar from '@this/components/appoint/open_meetings/big_calendar/big_calendar';
import localStorage from '@this/lib/local_storage';
import type { Plan } from '../../common';
import { Wrapper, Left, Right } from '../../common';

const OPEN_MEETING_KEY = 'openMeetingDataV1';

interface Response {
  need_auth: boolean;
  primary_calendar_id: string;
  calendars: CalendarArgs[];
  disconnected_accounts: { email: string; message: string | null }[];
  current_plan: Plan;
  trial_used: boolean;
  user_setting: UserSettingResponse;
  meeting_title_histories: MeetingTitleHistoryArgs[];
}

interface EventsResponse {
  events: EventResponse[][];
}

const createDefaultOpenMeeting = (paramName: string | null) => {
  const openMeeting = localStorage.getItem(OPEN_MEETING_KEY);
  if ((paramName === '' || paramName === null) && openMeeting !== null) {
    const raw: OpenMeetingResponseArgs = JSON.parse(openMeeting);
    // TODO: 参加者をlocalStorageに保存し、getItemするとメールアドレスが消えてしまう。（セキュリティ？）
    //       chromeで確認すると保存はされている。そのため一旦参加者は無視する。
    raw.participants = [];
    return new OpenMeeting(convertOpenMeetingResponseToArgs(raw));
  }
  return new OpenMeeting(initialOpenMeetingArgs);
};

const OpenMeetingsNew = observer(() => {
  const history = useHistory();
  const getParams = new URLSearchParams(window.location.search);
  let paramName = getParams.get('name');

  const contextValue = useContext(AppointContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [openMeeting] = useState<OpenMeeting>(createDefaultOpenMeeting(paramName));
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [disconnectedAccounts, setDisconnectedAccounts] = useState<{ email: string; message: string | null }[]>(
    []
  );
  const [eventLists, setEventLists] = useState<Event[][]>([[]]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [needAuth, setNeedAuth] = useState<boolean>(false);
  const [needReAuth, setNeedReAuth] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [date, setDate] = useState<Moment>(moment());
  const [isEventLoading, setIsEventLoading] = useState<boolean>(false);
  const [showBlockedSlotsForm, setShowBlockedSlotsForm] = useState<boolean>(false);
  const [currentPlan, setCurrentPlan] = useState<Plan>('free');
  const [trialUsed, setTrialUsed] = useState<boolean>(false);
  const [meetingTitleHistories, setMeetingTitleHistories] = useState<MeetingTitleHistory[]>([]);

  // 開始日にaddEndDateを加算した結果を終了日とする。終了日なしの場合は、一旦14日を設定する。
  // 候補日が不足する場合のための再試行処理がサーバー側にある。
  const addEndDate = 13;

  // カレンダーが月曜始まりなので、startMomentには「今週の月曜日」を表示したい。
  // 2021/06/06(日)時点では、2021/05/31(月)をstartMomentにセットしたい。
  // 2021/06/07(月)時点では、2021/06/07(月)をstartMomentにセットしたい。
  // day(1)は、曜日番号を1に変更するだけなので、日曜日(0)から見て前週の月曜日を取得するためには、
  // 1日戻る必要があるためsubtract(1, 'days')を設定している。
  const startMoment = date.clone().subtract(1, 'days').day(1);
  const endMoment = startMoment.clone().add(addEndDate, 'days');
  const startDate = startMoment.format('YYYY-MM-DD');
  const endDate = endMoment.format('YYYY-MM-DD');

  // 候補日自動計算用
  const [userSetting, setUserSetting] = useState<UserSetting>(UserSetting.defaultSetting);

  const substructEvent = () => {
    const newEventLists: Event[][] = [[]];
    openMeeting.eventlists.forEach(evts => {
      let existflg = false;
      evts.forEach(evt => {
        openMeeting.participants.forEach(cal => {
          if (cal.id === evt.calendarId) {
            existflg = true;
          }
        });
      });
      if (existflg) {
        newEventLists.push(evts);
      }
    });
    return newEventLists;
  };

  const fetchEvents = useCallback(async () => {
    if (openMeeting.participants.length === 0) {
      setEventLists([]);
      openMeeting.setEventlists([[]]);
      return;
    }
    setIsEventLoading(true);
    const tmpEventLists = substructEvent();
    const targetCalendars = openMeeting.participants;
    if (targetCalendars.length > 0) {
      const params = {
        calendars: targetCalendars.map(p => (p ? { id: p.id, auth_data_id: p.authDataId } : null)),
        from: startDate,
        to: endDate
      };
      const res = await axios.get<EventsResponse>('/aicalendar/events.json', { params });
      const data = res.data;
      const addEvents = data.events.map(evts => evts.map(e => new Event(convertEventResponseToArgs(e))));
      const existingIds = _.map(tmpEventLists.flat(), e => e.id);
      addEvents.forEach(dataOne => {
        tmpEventLists.push(_.filter(dataOne, d => existingIds.indexOf(d.id) === -1));
      });
    }
    setEventLists(tmpEventLists);
    openMeeting.setEventlists(tmpEventLists);
    setIsEventLoading(false);
  }, [openMeeting.participants, startDate, endDate]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const res = await axios.get<Response>('/aicalendar/open_meetings/new.json');
    setIsLoading(false);
    if (res.data.need_auth) {
      setNeedAuth(true);
      return;
    }
    setDisconnectedAccounts(res.data.disconnected_accounts);
    if (res.data.disconnected_accounts.length > 0) {
      setNeedReAuth(true);
      return;
    }

    const calendars = res.data.calendars.map(c => new Calendar(c));
    setCalendars(calendars);
    if (openMeeting.participants === null || openMeeting.participants.length === 0) {
      const primary = calendars.find(c => c.id === res.data.primary_calendar_id) || null;
      if (primary) {
        openMeeting.participants = [primary];
      }
    }
    if (openMeeting && paramName) {
      openMeeting.title = paramName;
      paramName = '';
    }
    setCurrentPlan(res.data.current_plan);
    setTrialUsed(res.data.trial_used);
    if (res.data.user_setting) {
      setUserSetting(new UserSetting(convertUserSettingResponseToArgs(res.data.user_setting)));
      openMeeting.meetingType = res.data.user_setting.meeting_type;
    }
    setMeetingTitleHistories(res.data.meeting_title_histories.map(raw => new MeetingTitleHistory(raw)));
  }, [openMeeting.participants]);

  // マウント時に1回発動
  useEffect(() => {
    fetchData();
    Utils.trackEvent('[cal][view] open_meetings new');
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [startDate, endDate, openMeeting.participants, openMeeting.openMeetingBlockedSlots]);

  const handleBlockedSlotRemove = (blockedSlot: OpenMeetingBlockedSlot) => {
    if (!showBlockedSlotsForm) {
      return;
    }
    openMeeting.removeBlockedSlot(blockedSlot);
  };

  const handleBlockedSlotsChange = (blockedSlots: OpenMeetingBlockedSlot[]) => {
    if (!showBlockedSlotsForm) {
      return;
    }
    openMeeting.openMeetingBlockedSlots = blockedSlots;
  };

  const handleSubmit = async () => {
    Utils.trackEvent('[cal][click] create open_meeting');
    setIsSubmitting(true);
    try {
      setErrorMessage(null);
      const res = await axios.post<OpenMeetingResponseArgs>(
        '/aicalendar/open_meetings.json',
        openMeeting.submitParams()
      );
      openMeeting.key = res.data.key;
      localStorage.removeItem(OPEN_MEETING_KEY);
      history.push('/#openMeetingCreated');
    } catch (e) {
      if (e.responseJSON && e.responseJSON.message) {
        setErrorMessage(e.responseJSON.message);
      } else {
        setErrorMessage('通信エラーが発生しました');
      }
    }
    setIsSubmitting(false);
  };

  const renderMobileCalendarDialog = () => (
    <Dialog
      open={showBlockedSlotsForm}
      maxWidth="lg"
      PaperProps={{
        style: {
          width: '100%',
          height: 'calc(100% - 30px - 20px)',
          maxHeight: 'calc(100% - 30px - 20px)',
          margin: '30px 10px 20px',
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif'
        }
      }}
      onClose={() => setShowBlockedSlotsForm(false)}
    >
      <Paper style={{ position: 'relative', maxWidth: '800px' }}>
        <Box display="flex" style={{ padding: 10 }}>
          <Typography style={{ marginRight: 'auto', fontSize: 14, fontWeight: 700 }}>
            除外日時をドラッグで選択
          </Typography>
          <IconButton size="small" onClick={() => setShowBlockedSlotsForm(false)}>
            <CloseButton fontSize="small" />
          </IconButton>
        </Box>
        <BigCalendar
          openMeeting={openMeeting}
          blockedSlots={openMeeting.openMeetingBlockedSlots}
          eventLists={eventLists}
          calendars={calendars}
          date={date}
          isEventLoading={isEventLoading}
          showBlockedSlotsForm={showBlockedSlotsForm}
          onDateChange={date => setDate(date)}
          openMeetingBlockedSlotsChange={blockedSlots => handleBlockedSlotsChange(blockedSlots)}
        />
        <MobileCalendarButtonWrap isMobile={contextValue.isMobile}>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={() => setShowBlockedSlotsForm(false)}
            style={{ width: '100%', padding: 10, background: '#fff' }}
          >
            閉じる
          </Button>
        </MobileCalendarButtonWrap>
      </Paper>
    </Dialog>
  );

  return (
    <TheWrapper isMobile={contextValue.isMobile}>
      <AuthBackdrop needAuth={needAuth} needReAuth={needReAuth} disconnectedAccounts={disconnectedAccounts} />
      <TheLeft isMobile={contextValue.isMobile}>
        <TheLeftBox isMobile={contextValue.isMobile}>
          <Form
            formTitle="公開URLを作成"
            isLoading={isLoading}
            openMeeting={openMeeting}
            calendars={calendars}
            currentPlan={currentPlan}
            onChangeParticipant={fetchEvents}
            openMeetingBlockedSlotRemove={blockedSlot => handleBlockedSlotRemove(blockedSlot)}
            openMeetingBlockedSlotsChange={blockedSlots => handleBlockedSlotsChange(blockedSlots)}
            isFetchingEvents={isEventLoading}
            showBlockedSlotsForm={showBlockedSlotsForm}
            setShowBlockedSlotsForm={setShowBlockedSlotsForm}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitErrorMessage={errorMessage}
            trialUsed={trialUsed}
            meetingTitleHistories={meetingTitleHistories}
            userSetting={userSetting}
          />
        </TheLeftBox>
      </TheLeft>
      {!contextValue.isMobile ? (
        <TheRight>
          <BigCalendar
            openMeeting={openMeeting}
            blockedSlots={openMeeting.openMeetingBlockedSlots}
            eventLists={eventLists}
            calendars={calendars}
            date={date}
            isEventLoading={isEventLoading}
            showBlockedSlotsForm={showBlockedSlotsForm}
            onDateChange={date => setDate(date)}
            openMeetingBlockedSlotsChange={blockedSlots => handleBlockedSlotsChange(blockedSlots)}
          />
          {showBlockedSlotsForm && (
            <BlockModeFoot display="flex">
              <BlockModeLabel>除外日時モード：</BlockModeLabel>
              <BlockModeText>カレンダーをドラッグして除外日を選択してください</BlockModeText>
            </BlockModeFoot>
          )}
        </TheRight>
      ) : (
        <>{renderMobileCalendarDialog()}</>
      )}
    </TheWrapper>
  );
});

export const TheWrapper = styled(Wrapper)<{ isMobile: boolean }>`
  min-height: calc(100vh - 54px);

  ${props =>
    props.isMobile &&
    `
    overflow-y: hidden;
  `}
`;

export const TheLeft = styled(Left)<{ isMobile: boolean }>`
  position: relative;
  width: 280px;
  margin: 0;
  padding-bottom: 24px;
  border-top: solid 10px ${muiTheme.palette.primary.main};
  border-right-color: #ededed;

  ${props =>
    props.isMobile &&
    `
    width: 100vw;
  `}
`;

export const TheLeftBox = styled.div<{ isMobile: boolean }>`
  max-height: calc(100vh - 54px - 10px - 67px);
  overflow-y: scroll;
`;

export const AutocompleteBox = styled.div<{ withAlert?: string }>`
  width: 100%;
  .MuiAutocomplete-inputRoot.MuiFilledInput-root {
    padding-top: 6px;
  }

  ${props =>
    props.withAlert === 'true' &&
    `
    input::placeholder {
      color: ${muiTheme.palette.error.main};
      opacity: 1;
    }
    .MuiInput-underline:before {
      border-bottom-color: ${muiTheme.palette.error.main};
    }
  `}
`;

export const TheRight = styled(Right)`
  position: relative;
  padding: 12px 20px 0 24px;
  border-left: solid 1px ${props => props.theme.grayBorderColor};

  .rbc-toolbar button {
    padding: 0.275rem 1rem;
  }
`;

export const BlockModeFoot = styled(Box)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 21px 0;
  background: #f9f9f9;
`;

export const BlockModeLabel = styled(Typography)`
  padding: 0 10px 0 20px;
  font-size: 14px;
  font-weight: bold;
  line-height: 1;
  color: ${muiTheme.palette.error.main};
`;

export const BlockModeText = styled(Typography)`
  font-size: 14px;
  line-height: 1;
`;

export const CloseButton = styled(CancelIcon)`
  color: #ddd;
`;

export const MobileCalendarButtonWrap = styled.div<{ isMobile: boolean }>`
  position: absolute;
  text-align: right;

  ${props =>
    props.isMobile &&
    `
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    box-sizing: border-box;
    width: 100%;
    padding: 10px 16px;
    background: #f9f9f9;
  `}
`;

export default OpenMeetingsNew;
